import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MarketingChannel,
  useAddMarketingSeoKeywordsFormMutation,
  useChangeMarketingPipelineKindMutation,
  useDecideMarketingSeoOpportunitiesMutation,
  useRemoveMarketingSeoQuestionMutation,
  useRunMarketingSeoJobMutation,
  useSaveMarketingSeoKeywordFlagsMutation,
  useSaveMarketingSeoQuestionFormMutation,
  useSaveMarketingSeoSettingsFormMutation,
  useCorrectMarketingSaleMutation,
  useDecideMarketingSignalMutation,
  usePinMarketingAdEntityMutation,
  useQueueMarketingDigestRebuildMutation,
  useRefreshMarketingSourcesNowMutation,
  useRemoveMarketingCampaignAliasMutation,
  useRemoveMarketingChannelRuleMutation,
  useRemoveMarketingCourseAliasMutation,
  useRemoveMarketingOfferRuleMutation,
  useSaveMarketingPageTypesFormMutation,
  useSetMarketingPlanTargetMutation,
  useStoreMarketingCampaignAliasMutation,
  useStoreMarketingChannelRuleMutation,
  useStoreMarketingCourseAliasMutation,
  useStoreMarketingCourseFamilyMutation,
  useStoreMarketingOfferRuleMutation,
  useStoreMarketingSearchSettingsMutation,
  useStoreMarketingSignalRoutingMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { IMarketingFieldError, toMarketingFieldErrors } from './marketingApi';
import { marketingSourceLabelKey } from './marketingModel';
import {
  IMarketingSeoKeywordsAdded,
  IMarketingSeoQuestionInput,
  IMarketingSeoRunResult,
  IMarketingSeoSettingsInput,
} from './seoTypes';
import {
  IMarketingCampaignAliasInput,
  IMarketingChannelRuleInput,
  IMarketingCourseAliasInput,
  IMarketingCourseFamilyInput,
  IMarketingOfferRuleInput,
  IMarketingPageTypeRule,
  IMarketingRefreshResult,
  IMarketingSaleOverrideInput,
  IMarketingSearchSettingsInput,
  IMarketingSignalRouting,
  IMarketingTargetInput,
} from './types';

/**
 * Коды отказа мутаций раздела (`customErrors.code`), у которых есть свой
 * перевод — в том же `marketing.errors.*`, что и отказы запросов (коды не
 * пересекаются). Незнакомый код показывается текстом сервера.
 */
export const MARKETING_MUTATION_ERROR_CODES = [
  'required',
  'too_long',
  'bad_id',
  'not_found',
  'bad_priority',
  'bad_regex',
  'bad_field',
  'bad_match',
  'bad_channel',
  'bad_offer',
  'bad_kind',
  'bad_funnel_kind',
  'bad_source',
  'bad_price',
  'slug',
  'slug_taken',
  'bad_sort',
  'family_not_found',
  'course_not_found',
  'course_required',
  'nested_quantifier',
  'backreference',
  'too_many',
  // Этап 2: план, сигналы, сопоставление кампаний.
  'bad_month',
  'bad_metric',
  'bad_family',
  'bad_value',
  'bad_status',
  'bad_platform',
  'not_campaign',
  'platform_mismatch',
  'alias_taken',
  // Этап 3: ключевые события GA4 и хосты сайта.
  'bad_event_name',
  'bad_host',
  // Этап 4: «Пересобрать» AI-сводку и маршрут «сигналы → задачи».
  'digest_disabled',
  'digest_running',
  // Пересборку ограничивает сервер: та же неделя — не чаще раза в 30 минут,
  // не больше 5 сводок в день.
  'digest_cooldown',
  'digest_daily_cap',
  'week_not_finished',
  'bad_week',
  'bad_severity',
  'bad_assignee',
  // ТЗ 0103, этап 3: код типа страницы.
  'bad_type',
  // ТЗ 0103, этап 4 — SEO: пустой список, запрос уже исключён, смета не
  // проходит бюджет, SEO выключено на сервере.
  'empty',
  'excluded',
  'seo_budget',
  'seo_disabled',
] as const;

export interface IMarketingMutationResult {
  ok: boolean;
  errors: IMarketingFieldError[];
}

interface IPayload {
  success?: boolean | null;
  customErrors?: ReadonlyArray<{
    field?: string | null;
    code?: string | null;
    message?: string | null;
  } | null> | null;
}

export interface IUseMarketingMutationsOptions {
  /**
   * Правка прошла: страница перечитывает справочники и ждёт пересчёта сводок
   * (`useMarketingRecalcWatch`).
   */
  onChanged?: () => void;
}

/**
 * Как правка доходит до цифр:
 *  - `recalc` — ставит пересборку (справочники, сопоставление кампаний):
 *    тост «пересчёт поставлен» и `onChanged` — страница ждёт `dataVersion`;
 *  - `instant` — сервер применил её до ответа (план поднимает версию сам,
 *    решение по сигналу читается без кеша): ждать нечего, вызывающий
 *    перечитывает свой запрос сразу, тост — без «пересчёта».
 */
type MarketingSaveMode = 'recalc' | 'instant';

interface ISettleOptions {
  mode?: MarketingSaveMode;
  /** Заголовок тоста успеха; по умолчанию «Сохранено». */
  successTitle?: string;
}

/**
 * ТЗ 0088 — правки раздела «Маркетинг»: справочники, вид воронки, привязка
 * рекламы, ручная правка продажи (Admin) и «Обновить сейчас» (Admin).
 *
 * Как у `Esign/useEsignMutations`, отказ бывает двух видов:
 *  - бизнес-ошибка в `customErrors` (промис успешен) — переводится
 *    `marketing.errors.<code>`, незнакомый код — текстом сервера;
 *  - отказ прав или брокера — GraphQL-ошибка, промис ОТКЛОНЯЕТСЯ; здесь он
 *    ловится, иначе клик остался бы без ответа.
 *
 * Тост поднимает хук. Результат `{ ok }` нужен форме: закрыться только после
 * успеха, а не терять введённое на отказе.
 *
 * Список справочников после правки НЕ перечитывается `refetchQueries` по
 * имени (грабли `refetch-by-name-skipped-query`): это делает `onChanged`
 * страницы явным `refetch` смонтированного запроса.
 */
export const useMarketingMutations = ({
  onChanged,
}: IUseMarketingMutationsOptions = {}) => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const [storeRule, { loading: l1 }] = useStoreMarketingChannelRuleMutation();
  const [removeRule, { loading: l2 }] = useRemoveMarketingChannelRuleMutation();
  const [
    storeFamily,
    { loading: l3 },
  ] = useStoreMarketingCourseFamilyMutation();
  const [storeAlias, { loading: l4 }] = useStoreMarketingCourseAliasMutation();
  const [
    removeAlias,
    { loading: l5 },
  ] = useRemoveMarketingCourseAliasMutation();
  const [storeOffer, { loading: l6 }] = useStoreMarketingOfferRuleMutation();
  const [removeOffer, { loading: l7 }] = useRemoveMarketingOfferRuleMutation();
  const [
    changePipeline,
    { loading: l8 },
  ] = useChangeMarketingPipelineKindMutation();
  const [pinAd, { loading: l9 }] = usePinMarketingAdEntityMutation();
  const [
    correctSale,
    { loading: correcting },
  ] = useCorrectMarketingSaleMutation();
  const [
    refreshNow,
    { loading: refreshing },
  ] = useRefreshMarketingSourcesNowMutation();
  const [storeTarget, { loading: l10 }] = useSetMarketingPlanTargetMutation();
  const [decideSignal, { loading: l11 }] = useDecideMarketingSignalMutation();
  const [
    storeCampaignAlias,
    { loading: l12 },
  ] = useStoreMarketingCampaignAliasMutation();
  const [
    removeCampaignAlias,
    { loading: l13 },
  ] = useRemoveMarketingCampaignAliasMutation();
  const [
    storeSearchSettings,
    { loading: savingSearch },
  ] = useStoreMarketingSearchSettingsMutation();
  const [
    queueDigest,
    { loading: queueingDigest },
  ] = useQueueMarketingDigestRebuildMutation();
  const [
    storeRouting,
    { loading: savingRouting },
  ] = useStoreMarketingSignalRoutingMutation();
  const [
    storePageTypes,
    { loading: savingPageTypes },
  ] = useSaveMarketingPageTypesFormMutation();
  // ТЗ 0103, этап 4 — SEO.
  const [
    storeSeoSettings,
    { loading: savingSeoSettings },
  ] = useSaveMarketingSeoSettingsFormMutation();
  const [
    storeSeoKeyword,
    { loading: savingSeoKeyword },
  ] = useSaveMarketingSeoKeywordFlagsMutation();
  const [
    addSeoKeywordsMutation,
    { loading: addingSeoKeywords },
  ] = useAddMarketingSeoKeywordsFormMutation();
  const [
    storeSeoQuestion,
    { loading: savingSeoQuestion },
  ] = useSaveMarketingSeoQuestionFormMutation();
  const [
    removeSeoQuestion,
    { loading: removingSeoQuestion },
  ] = useRemoveMarketingSeoQuestionMutation();
  const [
    decideSeoOpportunities,
    { loading: planningSeo },
  ] = useDecideMarketingSeoOpportunitiesMutation();
  const [
    runSeoJob,
    { loading: estimatingSeo },
  ] = useRunMarketingSeoJobMutation();

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) =>
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Marketing,
      }),
    [addToast],
  );

  const errorText = useCallback(
    (errors: IMarketingFieldError[]): string =>
      errors
        .map((error) =>
          error.code &&
          (MARKETING_MUTATION_ERROR_CODES as readonly string[]).includes(
            error.code,
          )
            ? t(`marketing.errors.${error.code}`)
            : error.message || error.code,
        )
        .filter(Boolean)
        .join('\n'),
    [t],
  );

  /**
   * Разбор ответа: успех — тост и (`recalc`) `onChanged` — ожидание
   * пересчёта; у `instant` пересчитывать нечего.
   */
  const settle = useCallback(
    (
      payload: IPayload | null | undefined,
      { mode = 'recalc', successTitle }: ISettleOptions = {},
    ): IMarketingMutationResult => {
      const errors = toMarketingFieldErrors(payload?.customErrors);
      if (payload?.success) {
        notify(
          ToastTypes.success,
          successTitle ?? t('marketing.toast.saved'),
          mode === 'recalc' ? t('marketing.toast.recalcQueued') : undefined,
        );
        if (mode === 'recalc') onChanged?.();
        return { ok: true, errors };
      }
      notify(
        ToastTypes.error,
        t('marketing.toast.failed'),
        errorText(errors) || undefined,
      );
      return { ok: false, errors };
    },
    [errorText, notify, onChanged, t],
  );

  const crash = useCallback(
    (error: unknown): IMarketingMutationResult => {
      const message = error instanceof Error ? error.message : '';
      notify(
        ToastTypes.error,
        t('marketing.toast.failed'),
        message || undefined,
      );
      return { ok: false, errors: [] };
    },
    [notify, t],
  );

  const run = useCallback(
    async (
      call: () => Promise<IPayload | null | undefined>,
      options?: ISettleOptions,
    ): Promise<IMarketingMutationResult> => {
      try {
        return settle(await call(), options);
      } catch (error) {
        return crash(error);
      }
    },
    [crash, settle],
  );

  const saveChannelRule = useCallback(
    (input: IMarketingChannelRuleInput) =>
      run(async () => {
        const { data } = await storeRule({
          variables: {
            input: {
              ...input,
              id: input.id || null,
              channel: input.channel as MarketingChannel,
            },
          },
        });
        return data?.saveMarketingChannelRule;
      }),
    [run, storeRule],
  );

  const deleteChannelRule = useCallback(
    (id: string) =>
      run(async () => {
        const { data } = await removeRule({ variables: { id } });
        return data?.deleteMarketingChannelRule;
      }),
    [run, removeRule],
  );

  const saveFamily = useCallback(
    (input: IMarketingCourseFamilyInput) =>
      run(async () => {
        const { data } = await storeFamily({
          variables: { input: { ...input, id: input.id || null } },
        });
        return data?.saveMarketingCourseFamily;
      }),
    [run, storeFamily],
  );

  const saveAlias = useCallback(
    (input: IMarketingCourseAliasInput) =>
      run(async () => {
        const { data } = await storeAlias({
          variables: {
            input: {
              ...input,
              id: input.id || null,
              courseId: input.courseId || null,
            },
          },
        });
        return data?.saveMarketingCourseAlias;
      }),
    [run, storeAlias],
  );

  const deleteAlias = useCallback(
    (id: string) =>
      run(async () => {
        const { data } = await removeAlias({ variables: { id } });
        return data?.deleteMarketingCourseAlias;
      }),
    [run, removeAlias],
  );

  const saveOfferRule = useCallback(
    (input: IMarketingOfferRuleInput) =>
      run(async () => {
        const { data } = await storeOffer({
          variables: { input: { ...input, id: input.id || null } },
        });
        return data?.saveMarketingOfferRule;
      }),
    [run, storeOffer],
  );

  const deleteOfferRule = useCallback(
    (id: string) =>
      run(async () => {
        const { data } = await removeOffer({ variables: { id } });
        return data?.deleteMarketingOfferRule;
      }),
    [run, removeOffer],
  );

  const setPipelineKind = useCallback(
    (id: string, funnelKind: string, locked: boolean) =>
      run(async () => {
        const { data } = await changePipeline({
          variables: { id, funnelKind, locked },
        });
        return data?.setMarketingPipelineKind;
      }),
    [run, changePipeline],
  );

  const overrideAdEntity = useCallback(
    (
      id: string,
      familySlug: string | null,
      offer: string | null,
      locked: boolean,
    ) =>
      run(async () => {
        const { data } = await pinAd({
          variables: { id, familySlug, offer, locked },
        });
        return data?.overrideMarketingAdEntity;
      }),
    [run, pinAd],
  );

  const overrideSale = useCallback(
    (input: IMarketingSaleOverrideInput) =>
      run(async () => {
        const { data } = await correctSale({ variables: { input } });
        return data?.overrideMarketingSale;
      }),
    [run, correctSale],
  );

  /**
   * «Обновить сейчас» (Admin): ставит сбор и пересборку, сам ничего не
   * качает. Тост говорит, что поставлено и что пропущено и почему.
   */
  const refreshSources = useCallback(
    async (sources?: string[]): Promise<IMarketingRefreshResult | null> => {
      const name = (code: string) => {
        const key = marketingSourceLabelKey(code);
        return key ? t(key) : code;
      };
      try {
        const { data } = await refreshNow({
          variables: { sources: sources?.length ? sources : null },
        });
        const payload = data?.refreshMarketingSources;
        const errors = toMarketingFieldErrors(payload?.customErrors);
        if (!payload?.success) {
          notify(
            ToastTypes.error,
            t('marketing.toast.refreshFailed'),
            errorText(errors) || undefined,
          );
          return null;
        }
        const result: IMarketingRefreshResult = {
          queued: payload.queued,
          skipped: payload.skipped,
          rebuildQueued: payload.rebuildQueued,
        };
        const lines = [
          result.queued.length
            ? t('marketing.toast.refreshQueued', {
                list: result.queued.map(name).join(', '),
              })
            : t('marketing.toast.refreshNothing'),
          ...result.skipped.map((item) =>
            t('marketing.toast.refreshSkipped', {
              name: name(item.source),
              reason: t(
                item.reason === 'running' || item.reason === 'disabled'
                  ? `marketing.toast.skipReasons.${item.reason}`
                  : 'marketing.toast.skipReasons.other',
              ),
            }),
          ),
          result.rebuildQueued ? t('marketing.toast.recalcQueued') : '',
        ].filter(Boolean);
        notify(
          result.queued.length || result.rebuildQueued
            ? ToastTypes.success
            : ToastTypes.warning,
          t('marketing.toast.refreshTitle'),
          lines.join('\n'),
        );
        if (result.queued.length || result.rebuildQueued) onChanged?.();
        return result;
      } catch (error) {
        crash(error);
        return null;
      }
    },
    [crash, errorText, notify, onChanged, refreshNow, t],
  );

  /**
   * Строка плана (`clear: true` — удалить). Сервер применяет её до ответа —
   * план-факт вызывающий перечитывает сам, ждать пересборки нечего.
   */
  const setTarget = useCallback(
    (input: IMarketingTargetInput) =>
      run(
        async () => {
          const { data } = await storeTarget({
            variables: {
              input: {
                month: input.month,
                channel: input.channel || null,
                familySlug: input.familySlug || null,
                metric: input.metric,
                value: input.clear ? null : input.value ?? null,
                note: input.note ?? null,
                clear: Boolean(input.clear),
              },
            },
          });
          return data?.setMarketingTarget;
        },
        {
          mode: 'instant',
          successTitle: input.clear
            ? t('marketing.toast.targetCleared')
            : t('marketing.toast.targetSaved'),
        },
      ),
    [run, storeTarget, t],
  );

  /** Решение по сигналу: `ack` — принято, `dismissed` — скрыть. */
  const ackSignal = useCallback(
    (id: string, status: 'ack' | 'dismissed') =>
      run(
        async () => {
          const { data } = await decideSignal({ variables: { id, status } });
          return data?.ackMarketingSignal;
        },
        {
          mode: 'instant',
          successTitle: t(
            status === 'ack'
              ? 'marketing.toast.signalAck'
              : 'marketing.toast.signalDismissed',
          ),
        },
      ),
    [run, decideSignal, t],
  );

  /** Метка CRM → кампания кабинета: ставит пересборку (`onChanged`). */
  const saveCampaignAlias = useCallback(
    (input: IMarketingCampaignAliasInput) =>
      run(async () => {
        const { data } = await storeCampaignAlias({
          variables: { input: { ...input, id: input.id || null } },
        });
        return data?.saveMarketingCampaignAlias;
      }),
    [run, storeCampaignAlias],
  );

  const deleteCampaignAlias = useCallback(
    (id: string) =>
      run(async () => {
        const { data } = await removeCampaignAlias({ variables: { id } });
        return data?.deleteMarketingCampaignAlias;
      }),
    [run, removeCampaignAlias],
  );

  /**
   * Этап 3 — регулярка бренда, ключевые события GA4 и хосты сайта. Смена
   * бренда или хостов переразмечает собранную историю поиска (`retag`:
   * пересчёт, страница ждёт `dataVersion`); одни события пересчёта не
   * ставят — они действуют на новые сборы GA4, вызывающий перечитывает
   * справочник сам.
   */
  const saveSearchSettings = useCallback(
    (input: IMarketingSearchSettingsInput, { retag }: { retag: boolean }) =>
      run(
        async () => {
          const { data } = await storeSearchSettings({
            variables: {
              brandRegex: input.brandRegex ?? null,
              ga4KeyEvents: input.ga4KeyEvents ?? null,
              siteHosts: input.siteHosts ?? null,
            },
          });
          return data?.saveMarketingSearchSettings;
        },
        { mode: retag ? 'recalc' : 'instant' },
      ),
    [run, storeSearchSettings],
  );

  /**
   * Этап 4 — «Пересобрать» AI-сводку недели (Admin, Marketing). Сервер
   * заводит строку `queued` и ставит задачу; ждать пересчёта сводок нечего —
   * вызывающий перечитывает историю сам и опрашивает статус.
   */
  const generateDigest = useCallback(
    (isoWeek: string | null) =>
      run(
        async () => {
          const { data } = await queueDigest({ variables: { isoWeek } });
          return data?.generateMarketingDigest;
        },
        {
          mode: 'instant',
          successTitle: t('marketing.toast.digestQueued'),
        },
      ),
    [run, queueDigest, t],
  );

  /**
   * Этап 4 — правило «сигналы → задачи» (только Admin). Применяется к
   * следующему ночному пересчёту сигналов; вызывающий перечитывает
   * справочник сам.
   */
  const saveSignalRouting = useCallback(
    (input: IMarketingSignalRouting) =>
      run(
        async () => {
          const { data } = await storeRouting({
            variables: {
              input: {
                kinds: input.kinds,
                minSeverity: input.minSeverity,
                assigneeIds: input.assigneeIds,
              },
            },
          });
          return data?.saveMarketingSignalRouting;
        },
        {
          mode: 'instant',
          successTitle: t('marketing.toast.routingSaved'),
        },
      ),
    [run, storeRouting, t],
  );

  /**
   * ТЗ 0103, этап 3 — правила типов страниц целиком (порядок — приоритет;
   * пустой список — правила по умолчанию). Сохранение переразмечает
   * справочник страниц и ставит пересборку: страница ждёт пересчёта.
   * Отказ — по полю правила (`rules.<i>.type|regex`), форма показывает его
   * у строки.
   */
  const savePageTypes = useCallback(
    (rules: ReadonlyArray<IMarketingPageTypeRule>) =>
      run(async () => {
        const { data } = await storePageTypes({
          variables: {
            rules: rules.map((rule) => ({
              type: rule.type,
              regex: rule.regex,
            })),
          },
        });
        return data?.saveMarketingPageTypes;
      }),
    [run, storePageTypes],
  );

  /**
   * ТЗ 0103, этап 4 — настройки SEO: набор, повторы AI, порог разрыва и
   * домены (свой, конкуренты; включить предложенный, выключить, регулярка
   * бренда). Сервер применяет сразу; справочник перечитывает вызывающий.
   */
  const saveSeoSettings = useCallback(
    (input: IMarketingSeoSettingsInput) =>
      run(
        async () => {
          const { data } = await storeSeoSettings({
            variables: {
              input: {
                maxTracked: input.maxTracked ?? null,
                aiRepeats: input.aiRepeats ?? null,
                minGapVolume: input.minGapVolume ?? null,
                // Список целиком — только когда его правили: иначе ключа нет
                // (сервер не трогает исключённые темы).
                ...(input.excludedTopics
                  ? { excludedTopics: input.excludedTopics }
                  : {}),
                domains: input.domains?.length
                  ? input.domains.map((domain) => ({
                      id: domain.id ?? null,
                      domain: domain.domain,
                      label: domain.label ?? null,
                      active: domain.active ?? null,
                      brandRegex: domain.brandRegex ?? null,
                      delete: domain.delete ?? null,
                    }))
                  : null,
              },
            },
          });
          return data?.saveMarketingSeoSettings;
        },
        { mode: 'instant' },
      ),
    [run, storeSeoSettings],
  );

  /** Исключить запрос из набора и разрывов (или вернуть; взять в набор). */
  const saveSeoKeyword = useCallback(
    (id: string, flags: { excluded?: boolean; tracked?: boolean }) =>
      run(
        async () => {
          const { data } = await storeSeoKeyword({
            variables: {
              id,
              excluded: flags.excluded ?? null,
              tracked: flags.tracked ?? null,
            },
          });
          return data?.saveMarketingSeoKeyword;
        },
        { mode: 'instant' },
      ),
    [run, storeSeoKeyword],
  );

  /**
   * Ручные запросы: ответ (`added` / `existing` / `tracked` / `rejected`)
   * нужен форме — сказать, что взято в набор, а что нет.
   */
  const addSeoKeywords = useCallback(
    async (
      keywords: ReadonlyArray<string>,
      familySlug: string | null,
    ): Promise<
      IMarketingMutationResult & { added: IMarketingSeoKeywordsAdded | null }
    > => {
      let added: IMarketingSeoKeywordsAdded | null = null;
      const result = await run(
        async () => {
          const { data } = await addSeoKeywordsMutation({
            variables: { keywords: [...keywords], familySlug },
          });
          const payload = data?.addMarketingSeoKeywords;
          if (payload?.success) {
            added = {
              added: payload.added ?? 0,
              existing: payload.existing ?? 0,
              tracked: payload.tracked ?? 0,
              rejected: payload.rejected ?? [],
            };
          }
          return payload;
        },
        { mode: 'instant' },
      );
      return { ...result, added };
    },
    [run, addSeoKeywordsMutation],
  );

  /** Вопрос AI: новый или правка (правка текста — новая версия). */
  const saveSeoQuestion = useCallback(
    (input: IMarketingSeoQuestionInput) =>
      run(
        async () => {
          const { data } = await storeSeoQuestion({
            variables: {
              input: {
                id: input.id ?? null,
                familySlug: input.familySlug ?? null,
                group: input.group,
                textPl: input.textPl,
                active: input.active ?? null,
                sort: input.sort ?? null,
              },
            },
          });
          return data?.saveMarketingSeoQuestion;
        },
        { mode: 'instant' },
      ),
    [run, storeSeoQuestion],
  );

  /** Удалить вопрос; с ответами сервер его выключает (история остаётся). */
  const deleteSeoQuestion = useCallback(
    (id: string) =>
      run(
        async () => {
          const { data } = await removeSeoQuestion({ variables: { id } });
          const payload = data?.deleteMarketingSeoQuestion;
          if (payload?.success && payload.deleted === false) {
            notify(ToastTypes.info, t('marketing.seo.settings.questionOff'));
          }
          return payload;
        },
        { mode: 'instant' },
      ),
    [run, removeSeoQuestion, notify, t],
  );

  /**
   * Решение по возможностям пакетом: «В план» (`planned`), «Сделано»,
   * «Скрыть», вернуть (`open`). Ответ несёт строки — статус обновляется в
   * кеше во всех списках.
   */
  const setSeoOpportunityStatus = useCallback(
    (ids: ReadonlyArray<string>, status: string, note?: string | null) =>
      run(
        async () => {
          const { data } = await decideSeoOpportunities({
            variables: { ids: [...ids], status, note: note ?? null },
          });
          return data?.setMarketingSeoOpportunityStatus;
        },
        {
          mode: 'instant',
          successTitle:
            status === 'planned'
              ? t('marketing.seo.plan.planned', { n: String(ids.length) })
              : undefined,
        },
      ),
    [run, decideSeoOpportunities, t],
  );

  /**
   * Admin: смета прогона DataForSEO — ТОЛЬКО сухой прогон (`dryRun: true`,
   * без внешних вызовов). Настоящего запуска из ERP нет: он платный, и
   * решение за владельцем. Смета, которая не проходит бюджет, — тоже ответ
   * (с `refusal`), а не ошибка.
   */
  const estimateSeoRun = useCallback(
    async (kind: string): Promise<IMarketingSeoRunResult | null> => {
      try {
        const { data } = await runSeoJob({
          variables: { kind, dryRun: true },
        });
        const payload = data?.runMarketingSeo ?? null;
        if (!payload?.estimate) {
          const errors = toMarketingFieldErrors(payload?.customErrors);
          notify(
            ToastTypes.error,
            t('marketing.toast.failed'),
            errorText(errors) || undefined,
          );
        }
        return payload;
      } catch (error) {
        crash(error);
        return null;
      }
    },
    [runSeoJob, notify, t, errorText, crash],
  );

  return {
    saving: l1 || l2 || l3 || l4 || l5 || l6 || l7 || l8 || l9 || l12 || l13,
    savingSeo:
      savingSeoSettings ||
      savingSeoKeyword ||
      addingSeoKeywords ||
      savingSeoQuestion ||
      removingSeoQuestion,
    planningSeo,
    estimatingSeo,
    saveSeoSettings,
    saveSeoKeyword,
    addSeoKeywords,
    saveSeoQuestion,
    deleteSeoQuestion,
    setSeoOpportunityStatus,
    estimateSeoRun,
    savingSearch,
    savingPageTypes,
    queueingDigest,
    savingRouting,
    savingTarget: l10,
    decidingSignal: l11,
    correcting,
    refreshing,
    saveChannelRule,
    deleteChannelRule,
    saveFamily,
    saveAlias,
    deleteAlias,
    saveOfferRule,
    deleteOfferRule,
    setPipelineKind,
    overrideAdEntity,
    overrideSale,
    refreshSources,
    setTarget,
    ackSignal,
    saveCampaignAlias,
    deleteCampaignAlias,
    saveSearchSettings,
    generateDigest,
    saveSignalRouting,
    savePageTypes,
  };
};

export type IMarketingMutations = ReturnType<typeof useMarketingMutations>;
